/** Desktop flyout menus, mobile drawer, cart proxy — runs inside `#header-component.header--dossier`. Sticky and `--header-height` stay on the theme header component. */
class HeaderDossier {
  /**
   * @param {HTMLElement} root
   */
  constructor(root) {
    this.root = root;
    /** @type {HTMLElement | null} */
    this.headerComponent = root.closest("#header-component");
    this.openMenu = null;
    /** @type {(() => void) | null} */
    this.closeMobileDrawer = null;
    this.boundDocumentClick = this.handleDocumentClick.bind(this);
    this.boundKeydown = this.handleKeydown.bind(this);
    this.boundResize = this.handleResize.bind(this);
    this.init();
  }

  init() {
    this.initDesktopMenus();
    this.initMobileDrawer();
    this.initMobileRow1ScrollReveal();
    this.initActionsPlacement();
    this.initSearchOpen();

    document.addEventListener("click", this.boundDocumentClick);
    document.addEventListener("keydown", this.boundKeydown);
    window.addEventListener("resize", this.boundResize);
  }

  initDesktopMenus() {
    const triggers = this.root.querySelectorAll(
      ".header-dossier__menu-trigger"
    );
    triggers.forEach((trigger) => {
      trigger.addEventListener("click", () => {
        const menu = trigger.closest(".header-dossier__menu");
        if (!menu) return;
        if (this.openMenu && this.openMenu !== menu)
          this.closeMenu(this.openMenu);
        if (menu.classList.contains("is-open")) {
          this.closeMenu(menu);
        } else {
          this.openDesktopMenu(menu);
        }
      });
    });
  }

  openDesktopMenu(menu) {
    const trigger = menu.querySelector(".header-dossier__menu-trigger");
    const dropdown = menu.querySelector(".header-dossier__dropdown");
    if (!trigger || !dropdown) return;

    menu.classList.add("is-open");
    trigger.setAttribute("aria-expanded", "true");
    dropdown.hidden = false;
    this.openMenu = menu;
    this.positionDropdown(menu);
    this.bindBranchButtons(menu);
  }

  /**
   * @param {HTMLElement} menu
   * @returns {HTMLElement | null}
   */
  #getSideContentMount(menu) {
    const side = menu.querySelector(".header-dossier__dropdown-side");
    if (!side) return null;
    return /** @type {HTMLElement | null} */ (
      side.querySelector("[data-hd-side-surface]")
    );
  }

  bindBranchButtons(menu) {
    const dropdown = menu.querySelector(".header-dossier__dropdown");
    const sidePanel = menu.querySelector(".header-dossier__dropdown-side");
    if (!dropdown || !sidePanel) return;

    dropdown
      .querySelectorAll(".header-dossier__dropdown-item--branch")
      .forEach((btn) => {
        if (btn.dataset.hdBranchBound === "true") return;
        btn.dataset.hdBranchBound = "true";
        btn.addEventListener("click", () => {
          const templateId = btn.getAttribute("data-side-panel-id");
          if (!templateId) return;
          const tpl = menu.querySelector(`#${CSS.escape(templateId)}`);
          if (!tpl || !(tpl instanceof HTMLTemplateElement)) return;

          const mount = this.#getSideContentMount(menu);
          if (mount) mount.innerHTML = tpl.innerHTML;
          sidePanel.hidden = false;
          this.positionDropdown(menu, btn);
        });
      });
  }

  positionDropdown(menu, anchorButton = null) {
    const dropdown = menu.querySelector(".header-dossier__dropdown");
    const sidePanel = menu.querySelector(".header-dossier__dropdown-side");
    if (!dropdown) return;

    const edgePad = 8;
    const parseLeft = () => Number.parseFloat(dropdown.style.left) || 0;

    dropdown.style.flexDirection = "row";
    dropdown.style.left = "0px";

    let rect = dropdown.getBoundingClientRect();
    let overflowRight = rect.right - window.innerWidth + edgePad;
    if (overflowRight > 0) {
      dropdown.style.left = `${Math.max(
        -rect.left + edgePad,
        -overflowRight - edgePad
      )}px`;
    }

    if (sidePanel && !sidePanel.hidden) {
      // If the full flyout (primary + side) still overflows the right edge, put the second column on the left.
      rect = dropdown.getBoundingClientRect();
      if (rect.right > window.innerWidth - edgePad) {
        dropdown.style.flexDirection = "row-reverse";
      } else {
        dropdown.style.flexDirection = "row";
      }

      rect = dropdown.getBoundingClientRect();
      if (rect.right > window.innerWidth - edgePad) {
        dropdown.style.left = `${
          parseLeft() - (rect.right - window.innerWidth + edgePad)
        }px`;
      }
      if (rect.left < edgePad) {
        dropdown.style.left = `${parseLeft() + (edgePad - rect.left)}px`;
      }

      if (anchorButton) {
        const parentRect = dropdown.getBoundingClientRect();
        const btnRect = anchorButton.getBoundingClientRect();
        sidePanel.style.marginTop = `${Math.max(
          0,
          btnRect.top - parentRect.top
        )}px`;
      }
    } else {
      dropdown.style.flexDirection = "row";
    }
  }

  closeMenu(menu) {
    const trigger = menu.querySelector(".header-dossier__menu-trigger");
    const dropdown = menu.querySelector(".header-dossier__dropdown");
    const sidePanel = menu.querySelector(".header-dossier__dropdown-side");
    const mount = this.#getSideContentMount(menu);

    menu.classList.remove("is-open");
    if (trigger) trigger.setAttribute("aria-expanded", "false");
    if (dropdown) {
      dropdown.hidden = true;
      dropdown.style.flexDirection = "row";
      dropdown.style.left = "";
    }
    if (sidePanel) {
      sidePanel.hidden = true;
      sidePanel.style.marginTop = "";
      if (mount) mount.innerHTML = "";
    }
    if (this.openMenu === menu) this.openMenu = null;
  }

  /**
   * One `header-actions` / cart-drawer instance: move it between desktop and mobile mounts by breakpoint.
   */
  initActionsPlacement() {
    const desktop = this.root.querySelector(
      ".header-dossier__actions-mount--desktop"
    );
    const mobile = this.root.querySelector(
      ".header-dossier__actions-mount--mobile"
    );
    if (!desktop || !mobile) return;

    const mq = window.matchMedia("(max-width: 989px)");

    const sync = () => {
      const from = mq.matches ? desktop : mobile;
      const to = mq.matches ? mobile : desktop;
      while (from.firstChild) {
        to.appendChild(from.firstChild);
      }
      const shell = this.root.querySelector(".header-dossier__actions-shell");
      desktop.classList.toggle("is-void", !desktop.firstElementChild);
      mobile.classList.toggle("is-void", !mobile.firstElementChild);
      shell?.classList.toggle("is-void", !desktop.firstElementChild);
    };

    sync();
    mq.addEventListener("change", sync);
  }

  /**
   * Opens the theme search modal. Calling click() on `<search-button>` does not run the inner
   * button’s `on:click="#search-modal/showDialog"` handler (synthetic click target is the host).
   * Call `dialog-component#search-modal`.showDialog() like the default header flow.
   */
  initSearchOpen() {
    const triggers = this.root.querySelectorAll("[data-dossier-search-open]");
    if (!triggers.length) return;

    triggers.forEach((btn) => {
      btn.addEventListener("click", (event) => {
        event.preventDefault();
        const modal = document.querySelector("#search-modal");
        if (modal && typeof modal.showDialog === "function") {
          modal.showDialog();
          return;
        }
        this.root.querySelector("search-button button")?.click();
      });
    });
  }

  /**
   * Keep mobile row1 visible on mobile.
   */
  initMobileRow1ScrollReveal() {
    const header = this.headerComponent;
    if (!header) return;
    header.setAttribute("data-hd-row1-revealed", "");
  }

  initMobileDrawer() {
    const drawer = this.headerComponent?.querySelector(
      ".header-dossier__mobile-drawer"
    );
    if (!drawer) return;

    const openBtn = this.root.querySelector("[data-mobile-drawer-open]");
    const closeBtns = drawer.querySelectorAll("[data-mobile-drawer-close]");
    const tabs = drawer.querySelectorAll(".header-dossier__mobile-tab");
    const panels = drawer.querySelectorAll(".header-dossier__mobile-panel");

    if (openBtn) {
      openBtn.addEventListener("click", () => {
        drawer.hidden = false;
        document.documentElement.setAttribute("scroll-lock", "");
        requestAnimationFrame(() => drawer.classList.add("is-open"));
        openBtn.setAttribute("aria-expanded", "true");
      });
    }

    const closeDrawer = () => {
      drawer.classList.remove("is-open");
      document.documentElement.removeAttribute("scroll-lock");
      if (openBtn) openBtn.setAttribute("aria-expanded", "false");
      setTimeout(() => {
        drawer.hidden = true;
      }, 260);
    };

    closeBtns.forEach((btn) => {
      btn.addEventListener("click", () => closeDrawer());
    });

    this.closeMobileDrawer = closeDrawer;

    tabs.forEach((tab) => {
      tab.addEventListener("click", () => {
        const key = tab.getAttribute("data-mobile-tab");
        if (!key) return;
        tabs.forEach((item) =>
          item.classList.toggle("is-active", item === tab)
        );
        panels.forEach((panel) => {
          panel.classList.toggle(
            "is-active",
            panel.getAttribute("data-mobile-panel") === key
          );
        });
      });
    });
  }

  handleDocumentClick(event) {
    if (!this.openMenu) return;
    if (
      event.target instanceof Node &&
      (this.openMenu.contains(event.target) ||
        event.target.closest(".header-dossier__dropdown-side"))
    ) {
      return;
    }
    this.closeMenu(this.openMenu);
  }

  handleKeydown(event) {
    if (event.key !== "Escape") return;
    const mobileDrawer = this.headerComponent?.querySelector(
      ".header-dossier__mobile-drawer"
    );
    if (mobileDrawer?.classList.contains("is-open") && this.closeMobileDrawer) {
      this.closeMobileDrawer();
      return;
    }
    if (this.openMenu) this.closeMenu(this.openMenu);
  }

  handleResize() {
    if (this.openMenu) this.positionDropdown(this.openMenu);
  }
}

function initHeaderDossier() {
  document
    .querySelectorAll("#header-component.header--dossier .header-dossier")
    .forEach((root) => {
      if (!(root instanceof HTMLElement)) return;
      if (root.dataset.hdInitialized === "true") return;
      root.dataset.hdInitialized = "true";
      new HeaderDossier(root);
    });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initHeaderDossier);
} else {
  initHeaderDossier();
}

// The header section is morphed by cart-items-component after every cart
// update (cart-items lives inside the header drawer with section_id =
// header section.id). The morph rewrites the dossier from server HTML where
// header-actions / cart-drawer sit in the *desktop* mount; on mobile that
// mount is hidden, so the actions appear "removed" until we re-run the
// dossier init on the fresh DOM.
//
// Re-running `initHeaderDossier` is unreliable because the new dossier root
// may keep the `data-hd-initialized` attribute through morph and because
// re-instantiating HeaderDossier leaks document listeners. Instead, just
// re-run the placement logic directly against whatever dossier is currently
// in the DOM. Deferred to the next task so that this runs *after* the
// section morph in cart-items-component (listener ordering on `document`
// is registration-order, not guaranteed).
function syncDossierActionsPlacement() {
  document
    .querySelectorAll(".header-dossier")
    .forEach((dossier) => {
      if (!(dossier instanceof HTMLElement)) return;
      const desktop = dossier.querySelector(
        ".header-dossier__actions-mount--desktop"
      );
      const mobile = dossier.querySelector(
        ".header-dossier__actions-mount--mobile"
      );
      if (!(desktop instanceof HTMLElement) || !(mobile instanceof HTMLElement))
        return;

      const isMobile = window.matchMedia("(max-width: 989px)").matches;
      const from = isMobile ? desktop : mobile;
      const to = isMobile ? mobile : desktop;

      // No-op when the source mount is already empty — important after
      // cart:update because cart-drawer auto-open fires synchronously and we
      // must not detach the drawer subtree (that would remove its <dialog>
      // from the top layer and close it).
      if (!from.firstChild) {
        const shell = dossier.querySelector(".header-dossier__actions-shell");
        desktop.classList.toggle("is-void", !desktop.firstElementChild);
        mobile.classList.toggle("is-void", !mobile.firstElementChild);
        shell?.classList.toggle("is-void", !desktop.firstElementChild);
        return;
      }

      // Capture any open <dialog> inside the subtree we're about to move so
      // we can re-open it after re-attachment (moving an open dialog removes
      // it from the top layer).
      /** @type {Array<HTMLElement>} */
      const drawersToReopen = [];
      from
        .querySelectorAll("cart-drawer-component")
        .forEach((cd) => {
          if (!(cd instanceof HTMLElement)) return;
          const dlg = cd.querySelector("dialog");
          if (dlg instanceof HTMLDialogElement && dlg.open) {
            drawersToReopen.push(cd);
          }
        });

      while (from.firstChild) {
        to.appendChild(from.firstChild);
      }

      const shell = dossier.querySelector(".header-dossier__actions-shell");
      desktop.classList.toggle("is-void", !desktop.firstElementChild);
      mobile.classList.toggle("is-void", !mobile.firstElementChild);
      shell?.classList.toggle("is-void", !desktop.firstElementChild);

      // Re-open drawers that were open before the move. Defer one frame so
      // disconnected/connectedCallbacks settle before we call showDialog.
      if (drawersToReopen.length > 0) {
        requestAnimationFrame(() => {
          drawersToReopen.forEach((cd) => {
            const showDialog = /** @type {{ showDialog?: () => void }} */ (cd)
              .showDialog;
            if (typeof showDialog === "function") {
              showDialog.call(cd);
            }
          });
        });
      }
    });
}

document.addEventListener("cart:update", (event) => {
  // What actually happens on a cart:update / CartAddEvent:
  //   1. cart-drawer-component#handleCartAdd schedules dialog.showModal() in
  //      a rAF on the *current* cart-drawer instance.
  //   2. cart-items-component#handleCartUpdate calls morphSection() on the
  //      header section. Because the live DOM has the actions in the *mobile*
  //      mount but the fresh server HTML puts them in the *desktop* mount,
  //      the morph reconciles parent-by-parent: it removes the old
  //      cart-drawer-component from the mobile mount (firing its
  //      disconnectedCallback) and inserts a brand-new one in the desktop
  //      mount. The new instance just connected -- it never saw the
  //      CartAddEvent -- so its auto-open never fires.
  //   3. The old instance's rAF eventually fires dialog.showModal() on a
  //      now-detached <dialog>, which throws InvalidStateError.
  //
  // Fix: after the morph settles, move the (new) actions back to the mobile
  // mount, then manually trigger showDialog on any auto-open drawer for the
  // events that should auto-open (i.e., cart adds, not quantity changes from
  // inside the cart drawer itself).
  const source = /** @type {{ detail?: { data?: { source?: string } } }} */ (
    /** @type {unknown} */ (event)
  ).detail?.data?.source;
  const isCartAdd = source !== "cart-items-component";

  requestAnimationFrame(() => {
    syncDossierActionsPlacement();

    if (!isCartAdd) return;

    // One more frame so our move's appendChild churn has settled and any
    // newly-connected cart-drawer-component instances have run their
    // connectedCallback.
    requestAnimationFrame(() => {
      document
        .querySelectorAll("cart-drawer-component[auto-open]")
        .forEach((cd) => {
          if (!(cd instanceof HTMLElement)) return;
          const dlg = cd.querySelector("dialog");
          if (dlg instanceof HTMLDialogElement && dlg.open) return;
          const showDialog = /** @type {{ showDialog?: () => void }} */ (cd)
            .showDialog;
          if (typeof showDialog === "function") {
            showDialog.call(cd);
          }
        });
    });
  });
});
